import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  McTopBar,
  McTable,
  McSelect,
  McOption,
  McInput,
  McModal,
} from "@maersk-global/mds-react-wrapper/components-core";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McIcon } from "@maersk-global/mds-react-wrapper/components-core/mc-icon";
import { jwtDecode } from "jwt-decode";

import { AL } from "../../al";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./Approvermatrix.css";

const EDITABLE_FIELDS = [
  "plant_name",
  "plant_no",
  "plant",
  "country",
  "city",
  "revised_approver",
  "po_creator",
  "approver_escalation_contact",
  "po_creator_escalation_contact",
  "organisation",
  "segment",
  "region",
  "approver_5k_10k",
  "mot_fcl",
  "mot_lcl",
  "mot_air",
  "mot_oog",
  "mot_mpl",
];

const HIDDEN_FIELDS = [];

// Audit / system columns — visible in the table but NOT editable
const READONLY_FIELDS = [
  "id",
  "change_log",
  "change_date",
  "created_at",
  "last_updated_by",
  "last_updated_timestamp",
  "modified_summary",
];

const DATE_FIELD_HINTS = [
  "timestamp",
  "date",
  "created_at",
  "updated_at",
];

// Fields allowed for bulk update.
// IMPORTANT: each excelCol (lowercased) must have a matching entry in the
// backend _PAM_BULK_FIELD_MAP so the bulk-update endpoint recognises the column.
const BULK_EDIT_FIELDS = [
  { label: "Plant Name",                   dbField: "plant_name",                    excelCol: "Plant Name" },
  { label: "Plant No",                     dbField: "plant_no",                      excelCol: "Plant No" },
  { label: "Plant",                        dbField: "plant",                         excelCol: "Plant" },
  { label: "Country",                      dbField: "country",                       excelCol: "Country" },
  { label: "City",                         dbField: "city",                          excelCol: "City" },
  { label: "Revised Approver",                  dbField: "revised_approver",              excelCol: "Revised Approver" },
  { label: "Po Creator",                        dbField: "po_creator",                    excelCol: "Po Creator" },
  { label: "Approver Escalation Contact",       dbField: "approver_escalation_contact",   excelCol: "Approver Escalation Contact" },
  { label: "Po Creator Escalation Contact",     dbField: "po_creator_escalation_contact", excelCol: "Po Creator Escalation Contact" },
  { label: "Organisation",                 dbField: "organisation",                  excelCol: "Organisation" },
  { label: "Segment",                      dbField: "segment",                       excelCol: "Segment" },
  { label: "Region",                       dbField: "region",                        excelCol: "Region" },
  { label: "Comments",                     dbField: "comments",                      excelCol: "Comments" },
  { label: "Approver 5k-10k",              dbField: "approver_5k_10k",               excelCol: "Approver 5k 10k" },
  { label: "MOT FCL",                      dbField: "mot_fcl",                       excelCol: "Mot Fcl" },
  { label: "MOT LCL",                      dbField: "mot_lcl",                       excelCol: "Mot Lcl" },
  { label: "MOT Air",                      dbField: "mot_air",                       excelCol: "Mot Air" },
  { label: "MOT OOG",                      dbField: "mot_oog",                       excelCol: "Mot Oog" },
  { label: "MOT MPL",                      dbField: "mot_mpl",                       excelCol: "Mot Mpl" },
];

// Module-level reference so lit-html cellTemplate closures always read the
// current selection set without needing it in the columns useMemo dependency array.
let __currentSelectedIds = new Set();


const Approvermatrix = () => {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyExporting, setHistoryExporting] = useState(false);
  const [fetchError, setFetchError] = useState("");

  const [roles, setRoles] = useState([]);
  const [userEmail, setUserEmail] = useState("");

  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 50,
    total_records: 0,
    total_pages: 0,
    has_next: false,
    has_previous: false,
  });

  const [editRow, setEditRow] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [editingFields, setEditingFields] = useState({});

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const [historyRow, setHistoryRow] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY_PAGE_SIZE = 25;

  const [addDrawerOpen, setAddDrawerOpen] = useState(false);
  const [addValues, setAddValues] = useState({});
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");
  const [addSuccess, setAddSuccess] = useState(false);

  // ── Search & bulk-edit state ─────────────────────────────────────────────
  const [searchQuery, setSearchQuery]           = useState("");
  const [activeSearch, setActiveSearch]         = useState("");
  const [searchLoading, setSearchLoading]       = useState(false);
  const [searchError, setSearchError]           = useState("");
  const [selectedRowIds, setSelectedRowIds]     = useState(new Set());
  const [bulkEditOpen, setBulkEditOpen]             = useState(false);
  const [bulkEditFields, setBulkEditFields]         = useState({});
  const [bulkEditValues, setBulkEditValues]         = useState({});
  const [bulkCurrentValues, setBulkCurrentValues]   = useState({});
  const [bulkUpdating, setBulkUpdating]             = useState(false);
  const [bulkUpdateResult, setBulkUpdateResult]     = useState(null);
  const [bulkUpdateError, setBulkUpdateError]       = useState("");

  const [tableKey, setTableKey] = useState(0);

  const editRowDataRef = useRef({});
  const historyModalRef = useRef(null);
  const historyTableWrapperRef = useRef(null);
  const paginationRef = useRef({
    page: 1,
    page_size: 50,
  });

  useEffect(() => {
    paginationRef.current = {
      page: pagination.page,
      page_size: pagination.page_size,
    };
  }, [pagination.page, pagination.page_size]);

  // Keep module-level var in sync so lit-html closures always see current selection.
  // Also force a table re-mount so checkboxes repaint when selection changes.
  useEffect(() => {
    __currentSelectedIds = selectedRowIds;
    if (activeSearch) {
      setTableKey((k) => k + 1);
    }
  }, [selectedRowIds, activeSearch]);

  /*
   * Decode the access token and retrieve user roles.
   */
  useEffect(() => {
    const tokenKey = process.env.REACT_APP_CIAM_ACCESS_KEY;
    const token = localStorage.getItem(tokenKey);

    if (!token) {
      return;
    }

    try {
      const decoded = jwtDecode(token);
      setRoles(decoded.roles || []);
      setUserEmail(decoded.email || "");
    } catch (error) {
      console.error("Invalid access token:", error);
      setRoles([]);
    }
  }, []);

  const canEdit = useMemo(() => {
    const adminEmails = (process.env.REACT_APP_APPROVER_MATRIX_ADMIN_EMAILS || "")
      .split(",")
      .map((e) => e.trim().toLowerCase())
      .filter(Boolean);
    return adminEmails.includes(userEmail.toLowerCase());
  }, [userEmail]);

  const canDelete = useMemo(() => {
    const adminEmails = (process.env.REACT_APP_APPROVER_MATRIX_ADMIN_EMAILS || "")
      .split(",")
      .map((e) => e.trim().toLowerCase())
      .filter(Boolean);
    return adminEmails.includes(userEmail.toLowerCase());
  }, [userEmail]);

  const getAuthHeaders = useCallback(() => {
    const tokenKey = process.env.REACT_APP_CIAM_ACCESS_KEY;
    const token = localStorage.getItem(tokenKey);

    return {
      Authorization: `Bearer ${token}`,
      "consumer-key":
        process.env.REACT_APP_FORGEROCK_CONSUMER_KEY,
    };
  }, []);

  const sortRowsById = useCallback((rows) => {
    return [...rows].sort((firstRow, secondRow) => {
      const firstId = Number(firstRow.id);
      const secondId = Number(secondRow.id);

      if (
        !Number.isNaN(firstId) &&
        !Number.isNaN(secondId)
      ) {
        return firstId - secondId;
      }

      return String(firstRow.id).localeCompare(
        String(secondRow.id),
        undefined,
        {
          numeric: true,
          sensitivity: "base",
        }
      );
    });
  }, []);

  /*
   * Fetch the requested database page.
   *
   * The timestamp and cache:no-store prevent a cached GET result
   * from being displayed after an update.
   */
  const fetchData = useCallback(
    async (page, pageSize) => {
      const requestedPage =
        page ?? paginationRef.current.page;

      const requestedPageSize =
        pageSize ?? paginationRef.current.page_size;

      setLoading(true);
      setFetchError("");

      try {
        const params = new URLSearchParams({
          page: String(requestedPage),
          page_size: String(requestedPageSize),
          _timestamp: String(Date.now()),
        });

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/plant-approver-matrix?${params.toString()}`,
          {
            method: "GET",
            headers: {
              ...getAuthHeaders(),
              "Cache-Control": "no-cache",
              Pragma: "no-cache",
            },
            cache: "no-store",
          }
        );

        const responseBody = await response
          .json()
          .catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            responseBody.detail ||
              responseBody.message ||
              `Unable to load records. HTTP ${response.status}`
          );
        }

        const rows = Array.isArray(responseBody.data)
          ? responseBody.data
          : [];

        const orderedRows = sortRowsById(rows);

        setData(orderedRows);

        setPagination(
          responseBody.pagination || {
            page: requestedPage,
            page_size: requestedPageSize,
            total_records: orderedRows.length,
            total_pages: 1,
            has_next: false,
            has_previous: false,
          }
        );

        const rowLookup = {};

        orderedRows.forEach((row) => {
          rowLookup[String(row.id)] = row;
        });

        editRowDataRef.current = rowLookup;

        /*
         * McTable may internally retain old row state.
         * Updating the key forces it to render the new data.
         */
        setTableKey((previousKey) => previousKey + 1);

        return orderedRows;
      } catch (error) {
        console.error(
          "Error fetching plant approver matrix:",
          error
        );

        setFetchError(
          error.message ||
            "Unable to load plant approver matrix."
        );

        throw error;
      } finally {
        setLoading(false);
      }
    },
    [getAuthHeaders, sortRowsById]
  );

  useEffect(() => {
    fetchData(1, 50).catch(() => {
      // Error is already displayed by fetchData.
    });
  }, [fetchData]);

  const fetchSearchResults = useCallback(
    async (query, page, pageSize) => {
      setSearchLoading(true);
      setSearchError("");
      try {
        const params = new URLSearchParams({ query: query.trim() });
        params.set("page", String(page));
        params.set("page_size", String(pageSize));
        params.set("_timestamp", String(Date.now()));
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/plant-approver-matrix/search?${params.toString()}`,
          {
            method: "GET",
            headers: { ...getAuthHeaders(), "Cache-Control": "no-cache", Pragma: "no-cache" },
            cache: "no-store",
          }
        );
        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(body.detail || body.message || `HTTP ${response.status}`);
        }
        const rows = Array.isArray(body.data) ? body.data : [];
        const ordered = sortRowsById(rows);
        setData(ordered);
        setPagination(
          body.pagination || {
            page,
            page_size: pageSize,
            total_records: ordered.length,
            total_pages: 1,
            has_next: false,
            has_previous: false,
          }
        );
        const lookup = {};
        ordered.forEach((r) => { lookup[String(r.id)] = r; });
        editRowDataRef.current = lookup;
        setTableKey((k) => k + 1);
      } catch (err) {
        setSearchError(err.message || "Search failed.");
      } finally {
        setSearchLoading(false);
      }
    },
    [getAuthHeaders, sortRowsById]
  );

  const openEdit = useCallback((row) => {
    const initialValues = {
      comments: "",
    };

    EDITABLE_FIELDS.forEach((field) => {
      initialValues[field] = row[field] ?? "";
    });

    setEditRow(row);
    setEditValues(initialValues);
    setEditingFields({});
    setSaveError("");
    setSaveSuccess(false);

    requestAnimationFrame(() => {
      setDrawerOpen(true);
    });
  }, []);

  const closeEdit = useCallback(() => {
    if (saving) {
      return;
    }

    setDrawerOpen(false);

    window.setTimeout(() => {
      setEditRow(null);
      setEditValues({});
      setEditingFields({});
      setSaveError("");
      setSaveSuccess(false);
    }, 300);
  }, [saving]);

  /*
   * Receive edit events from McTable's cell template.
   */
  useEffect(() => {
    const handleOpenEditEvent = (event) => {
      const rowId = event.detail?.rowId;

      const selectedRow =
        editRowDataRef.current[String(rowId)];

      if (selectedRow) {
        openEdit(selectedRow);
      }
    };

    window.addEventListener(
      "openMatrixEdit",
      handleOpenEditEvent
    );

    return () => {
      window.removeEventListener(
        "openMatrixEdit",
        handleOpenEditEvent
      );
    };
  }, [openEdit]);

  /*
   * Receive delete events from McTable's cell template.
   */
  useEffect(() => {
    const handleDeleteEvent = (event) => {
      const rowId = event.detail?.rowId;

      const selectedRow =
        editRowDataRef.current[String(rowId)];

      if (selectedRow) {
        setDeleteRow(selectedRow);
        setDeleteError("");
      }
    };

    window.addEventListener(
      "deleteMatrixRow",
      handleDeleteEvent
    );

    return () => {
      window.removeEventListener(
        "deleteMatrixRow",
        handleDeleteEvent
      );
    };
  }, []);

  /*
   * Toggle row selection (dispatched by checkbox cellTemplate).
   */
  useEffect(() => {
    const handler = (e) => {
      const rowId = String(e.detail?.rowId ?? "");
      if (!rowId) return;
      setSelectedRowIds((prev) => {
        const next = new Set(prev);
        if (next.has(rowId)) next.delete(rowId);
        else next.add(rowId);
        return next;
      });
    };
    window.addEventListener("toggleRowSelection", handler);
    return () => window.removeEventListener("toggleRowSelection", handler);
  }, []);

  const handleDelete = async () => {
    if (!deleteRow || deleting) {
      return;
    }

    setDeleting(true);
    setDeleteError("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/plant-approver-matrix/${deleteRow.id}`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        }
      );

      const responseBody = await response
        .json()
        .catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          responseBody.detail ||
            responseBody.message ||
            `Delete failed. HTTP ${response.status}`
        );
      }

      setDeleteRow(null);

      await fetchData(
        paginationRef.current.page,
        paginationRef.current.page_size
      );
    } catch (error) {
      console.error(
        "Error deleting plant approver record:",
        error
      );

      setDeleteError(
        error.message ||
          "Failed to delete the record."
      );
    } finally {
      setDeleting(false);
    }
  };

  const fetchHistory = useCallback(async (recordId) => {
    setHistoryLoading(true);
    setHistoryError("");
    setHistoryData([]);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/plant-approver-matrix/${recordId}/history`,
        {
          method: "GET",
          headers: { ...getAuthHeaders(), "Cache-Control": "no-cache", Pragma: "no-cache" },
          cache: "no-store",
        }
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.detail || body.message || `HTTP ${response.status}`);
      }
      setHistoryData(Array.isArray(body.history) ? body.history : []);
      if (historyTableWrapperRef.current) historyTableWrapperRef.current.scrollTop = 0;
    } catch (err) {
      setHistoryError(err.message || "Failed to load history.");
    } finally {
      setHistoryLoading(false);
    }
  }, [getAuthHeaders]);

  /*
   * Receive history events from McTable's cell template.
   */
  useEffect(() => {
    const handler = (event) => {
      const rowId = event.detail?.rowId;
      const selectedRow = editRowDataRef.current[String(rowId)];
      if (selectedRow) {
        setHistoryRow(selectedRow);
        setHistoryOpen(true);
        setHistoryPage(1);
        fetchHistory(rowId);
      }
    };
    window.addEventListener("openMatrixHistory", handler);
    return () => window.removeEventListener("openMatrixHistory", handler);
  }, [fetchHistory]);

  /*
   * Construct a request containing only changed fields.
   */
  const buildUpdatePayload = useCallback(() => {
    if (!editRow) {
      return {};
    }

    const payload = {};

    EDITABLE_FIELDS.forEach((field) => {
      const oldValue = editRow[field] ?? "";
      const newValue = editValues[field] ?? "";

      const normalizedOldValue =
        String(oldValue).trim();

      const normalizedNewValue =
        String(newValue).trim();

      if (
        normalizedOldValue !== normalizedNewValue
      ) {
        payload[field] = newValue;
      }
    });

    const comments = String(
      editValues.comments ?? ""
    ).trim();

    if (comments) {
      payload.comments = comments;
    }

    return payload;
  }, [editRow, editValues]);

  const handleSave = async () => {
    console.log("SAVE BUTTON CLICKED");

    if (saving) {
      return;
    }

    if (!editRow) {
      setSaveError(
        "No row was selected for editing."
      );
      return;
    }

    setSaving(true);
    setSaveError("");
    setSaveSuccess(false);

    try {
      const updatePayload = buildUpdatePayload();

      console.log("Record ID:", editRow.id);
      console.log("Original record:", editRow);
      console.log("Edited values:", editValues);
      console.log("PUT payload:", updatePayload);

      if (
        Object.keys(updatePayload).length === 0
      ) {
        throw new Error(
          "No change was detected. Please edit a value before saving."
        );
      }

      const updateUrl =
        `${process.env.REACT_APP_API_BASE_URL}` +
        `/plant-approver-matrix/${editRow.id}`;

      console.log("PUT URL:", updateUrl);

      const response = await fetch(updateUrl, {
        method: "PUT",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatePayload),
      });

      const responseBody = await response
        .json()
        .catch(() => ({}));

      console.log("PUT status:", response.status);
      console.log("PUT response:", responseBody);

      if (!response.ok) {
        throw new Error(
          responseBody.detail ||
            responseBody.message ||
            `Update failed. HTTP ${response.status}`
        );
      }

      const updatedRow = responseBody.data;

      if (!updatedRow || updatedRow.id == null) {
        throw new Error(
          "The backend did not return the updated database row."
        );
      }

      /*
       * Replace the updated row immediately in React state.
       */
      setData((previousRows) => {
        const updatedRows = previousRows.map(
          (row) =>
            String(row.id) ===
            String(updatedRow.id)
              ? updatedRow
              : row
        );

        return sortRowsById(updatedRows);
      });

      /*
       * Update the lookup used by the Edit and Delete buttons.
       */
      editRowDataRef.current = {
        ...editRowDataRef.current,
        [String(updatedRow.id)]: updatedRow,
      };

      setEditRow(updatedRow);
      setSaveSuccess(true);

      /*
       * Fetch the same page again and wait for it.
       * This confirms the value persisted in the database.
       */
      await fetchData(
        paginationRef.current.page,
        paginationRef.current.page_size
      );

      window.setTimeout(() => {
        setDrawerOpen(false);

        window.setTimeout(() => {
          setEditRow(null);
          setEditValues({});
          setEditingFields({});
          setSaveError("");
          setSaveSuccess(false);
        }, 300);
      }, 1000);
    } catch (error) {
      console.error("SAVE ERROR:", error);

      setSaveError(
        error.message ||
          "Failed to save changes."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    AL.logoutRedirect({
      post_logout_uri:
        `${window.location.origin}/`,
    });
  };

  const openAddDrawer = () => {
    const empty = {};
    EDITABLE_FIELDS.forEach((f) => { empty[f] = ""; });
    empty.comments = "";
    setAddValues(empty);
    setAddError("");
    setAddSuccess(false);
    setAddDrawerOpen(true);
  };

  const closeAddDrawer = () => {
    if (adding) return;
    setAddDrawerOpen(false);
    window.setTimeout(() => {
      setAddValues({});
      setAddError("");
      setAddSuccess(false);
    }, 300);
  };

  const handleAddSave = async () => {
    if (adding) return;
    setAdding(true);
    setAddError("");
    setAddSuccess(false);

    try {
      const payload = {};
      EDITABLE_FIELDS.forEach((f) => {
        if (addValues[f] !== undefined && String(addValues[f]).trim() !== "") {
          payload[f] = addValues[f];
        }
      });
      if (addValues.comments && String(addValues.comments).trim() !== "") {
        payload.comments = addValues.comments;
      }

      // ── Duplicate check against loaded records ───────────────────────
      const norm = (v) => String(v ?? "").trim().toLowerCase();
      const newPlantName = norm(addValues.plant_name);
      const newPlantNo   = norm(addValues.plant_no);
      const newCountry   = norm(addValues.country);
      const newCity      = norm(addValues.city);

      const duplicate = data.find((row) =>
        norm(row.plant_name) === newPlantName &&
        norm(row.plant_no)   === newPlantNo   &&
        norm(row.country)    === newCountry   &&
        norm(row.city)       === newCity
      );

      if (duplicate) {
        throw new Error(
          `A record with the same Plant Name "${addValues.plant_name}", Plant No "${addValues.plant_no}", Country "${addValues.country}", and City "${addValues.city}" already exists (ID: ${duplicate.id}). Please use a unique combination.`
        );
      }
      // ────────────────────────────────────────────────────────────────

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/plant-approver-matrix`,
        {
          method: "POST",
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        // FastAPI 422 returns detail as array of validation error objects
        const detail = body.detail;
        const errorMsg = Array.isArray(detail)
          ? detail.map((e) => `${e.loc?.slice(-1)[0] ?? "field"}: ${e.msg}`).join(", ")
          : detail || body.message || `HTTP ${response.status}`;
        throw new Error(errorMsg);
      }

      setAddSuccess(true);

      await fetchData(
        paginationRef.current.page,
        paginationRef.current.page_size
      );

      window.setTimeout(() => {
        setAddDrawerOpen(false);
        window.setTimeout(() => {
          setAddValues({});
          setAddError("");
          setAddSuccess(false);
        }, 300);
      }, 1000);
    } catch (error) {
      console.error("Add error:", error);
      setAddError(error.message || "Failed to add record.");
    } finally {
      setAdding(false);
    }
  };

  // Auto-generate the Plant field when adding a new record
  useEffect(() => {
    if (!addDrawerOpen) return;
    const parts = [
      addValues.plant_name,
      addValues.plant_no,
      addValues.country,
      addValues.city,
    ].map((v) => String(v ?? "").trim()).filter(Boolean);
    const generated = parts.join(" - ");
    setAddValues((prev) => ({ ...prev, plant: generated }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addValues.plant_name, addValues.plant_no, addValues.country, addValues.city, addDrawerOpen]);

  // Auto-generate the Plant field in bulk edit when source fields are set
  useEffect(() => {
    if (!bulkEditOpen) return;
    const parts = [
      bulkEditValues.plant_name,
      bulkEditValues.plant_no,
      bulkEditValues.country,
      bulkEditValues.city,
    ].map((v) => String(v ?? "").trim()).filter(Boolean);
    if (parts.length === 0) return;
    const generated = parts.join(" - ");
    setBulkEditValues((prev) => ({ ...prev, plant: generated }));
    setBulkEditFields((prev) => ({ ...prev, plant: true }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkEditValues.plant_name, bulkEditValues.plant_no, bulkEditValues.country, bulkEditValues.city, bulkEditOpen]);

  // ── Search handlers ───────────────────────────────────────────────────────
  const handleSearch = () => {
    const q = searchQuery.trim();
    if (q.length < 2) return;
    setActiveSearch(q);
    setSelectedRowIds(new Set());
    setBulkUpdateResult(null);
    setBulkUpdateError("");
    fetchSearchResults(q, 1, pagination.page_size);
  };

  const handleSearchClear = () => {
    setSearchQuery("");
    setActiveSearch("");
    setSelectedRowIds(new Set());
    setBulkUpdateResult(null);
    setBulkUpdateError("");
    fetchData(1, pagination.page_size).catch(() => {});
  };

  const handleSelectAll = () => {
    if (selectedRowIds.size === data.length && data.length > 0) {
      setSelectedRowIds(new Set());
    } else {
      setSelectedRowIds(new Set(data.map((r) => String(r.id))));
    }
  };

  // ── Bulk edit handlers ────────────────────────────────────────────────────
  const openBulkEdit = () => {
    const selectedRows = data.filter((r) => selectedRowIds.has(String(r.id)));

    // Compute the current value for every bulk-edit field across selected rows.
    // If all selected rows share the same value → show it; otherwise store all unique values.
    const currentVals = {};
    BULK_EDIT_FIELDS.forEach((field) => {
      const values = selectedRows.map((r) =>
        String(r[field.dbField] ?? "").trim()
      );
      const unique = [...new Set(values)];
      currentVals[field.dbField] = {
        same: unique.length <= 1,
        value: unique.length === 1 ? unique[0] : null,
        uniqueValues: unique,
      };
    });

    setBulkCurrentValues(currentVals);
    setBulkEditFields({});
    setBulkEditValues({});
    setBulkUpdateResult(null);
    setBulkUpdateError("");
    setBulkEditOpen(true);
  };

  const closeBulkEdit = () => {
    if (bulkUpdating) return;
    setBulkEditOpen(false);
  };

  const handleBulkUpdate = async () => {
    if (bulkUpdating) return;

    const selectedFields = BULK_EDIT_FIELDS.filter(
      (f) => bulkEditFields[f.dbField] && String(bulkEditValues[f.dbField] ?? "").trim()
    );
    if (selectedFields.length === 0) {
      setBulkUpdateError("Select at least one field and provide a non-empty value.");
      return;
    }

    const selectedRows = data.filter((r) => selectedRowIds.has(String(r.id)));
    if (selectedRows.length === 0) {
      setBulkUpdateError("No rows selected.");
      return;
    }

    // Build Excel payload and POST to bulk-update endpoint.
    // "Plant No" is always the identifier column (first). Any other selected
    // fields follow. If the user also checked "Plant No" as an editable field
    // the column is already present, so we deduplicate.
    const extraCols = selectedFields.map((f) => f.excelCol).filter((c) => c !== "Plant No");
    const headers = ["Plant No", ...extraCols];
    const excelRows = selectedRows.map((r) => {
      const row = {};
      selectedFields.forEach((f) => { row[f.excelCol] = String(bulkEditValues[f.dbField]).trim(); });
      row["Plant No"] = r.plant_no; // identifier — always the original value, written last
      return row;
    });

    const ws = XLSX.utils.json_to_sheet(excelRows, { header: headers });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bulk Update");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const formData = new FormData();
    formData.append("file", blob, "bulk_update.xlsx");

    setBulkUpdating(true);
    setBulkUpdateResult(null);
    setBulkUpdateError("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/plant-approver-matrix/bulk-update`,
        { method: "POST", headers: getAuthHeaders(), body: formData }
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.detail || body.message || `HTTP ${response.status}`);
      setBulkUpdateResult(body);
      setBulkEditOpen(false);
      setSelectedRowIds(new Set());
      if (activeSearch) {
        await fetchSearchResults(activeSearch, 1, paginationRef.current.page_size);
      } else {
        await fetchData(1, paginationRef.current.page_size);
      }
    } catch (err) {
      setBulkUpdateError(err.message || "Bulk update failed.");
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleHistoryExport = async () => {
    try {
      setHistoryExporting(true);

      // Fetch the history collection directly so deleted records are included.
      let allHistoryEntries = [];
      let currentPage = 1;
      const pageSize = 500;
      let hasMore = true;

      while (hasMore) {
        const params = new URLSearchParams({
          page: String(currentPage),
          page_size: String(pageSize),
          _timestamp: String(Date.now()),
        });
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/plant-approver-matrix/history?${params.toString()}`,
          {
            method: "GET",
            headers: { ...getAuthHeaders(), "Cache-Control": "no-cache", Pragma: "no-cache" },
            cache: "no-store",
          }
        );
        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(body.detail || body.message || `HTTP ${response.status}`);
        }
        const entries = Array.isArray(body.history)
          ? body.history
          : Array.isArray(body.data)
            ? body.data
            : [];
        allHistoryEntries = allHistoryEntries.concat(entries);
        hasMore = body.pagination?.has_next || false;
        currentPage++;
      }

      if (allHistoryEntries.length === 0) {
        alert("No history records found.");
        return;
      }

      const currentRows = [];
      let matrixPage = 1;
      let matrixHasMore = true;
      while (matrixHasMore) {
        const params = new URLSearchParams({ page: String(matrixPage), page_size: String(pageSize) });
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/plant-approver-matrix?${params.toString()}`,
          { headers: getAuthHeaders(), cache: "no-store" }
        );
        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(body.detail || body.message || `HTTP ${response.status}`);
        }
        currentRows.push(...(Array.isArray(body.data) ? body.data : []));
        matrixHasMore = body.pagination?.has_next || false;
        matrixPage++;
      }

      const bulkRows = currentRows.flatMap((row) => {
        const summary = String(row.modified_summary || "");
        const isBulk = /bulk/i.test(row.comments || "") || /bulk/i.test(row.change_log || "") || /bulk/i.test(summary);
        if (!isBulk) return [];
        const base = {
          "Plant Name": row.plant_name || "",
          "Plant No": row.plant_no || "",
          "Plant": row.plant || "",
          "Country": row.country || "",
          "City": row.city || "",
          "Edit Type": "Bulk Edit",
          "Changed By": row.last_updated_by || "",
          "Date & Time": row.last_updated_timestamp
            ? new Date(row.last_updated_timestamp).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })
            : "",
        };
        const entries = summary.split(",").map((value) => value.trim()).filter(Boolean);
        return (entries.length ? entries : ["Multiple fields"]).map((value) => {
          const colonIndex = value.indexOf(":");
          const field = colonIndex === -1 ? value : value.slice(0, colonIndex).trim();
          const change = colonIndex === -1 ? "" : value.slice(colonIndex + 1).trim();
          const arrowIndex = change.search(/→|->|\?/);
          return {
            ...base,
            "Field Changed": field.replace(/_/g, " "),
            "Old Value": arrowIndex === -1 ? "" : change.slice(0, arrowIndex).trim(),
            "New Value": arrowIndex === -1 ? change || row.comments || "" : change.slice(arrowIndex + 1).replace(/^\s*[>]?\s*/, "").trim(),
          };
        });
      });

      const currentRowsById = new Map(currentRows.map((row) => [String(row.id), row]));
      const historyContextById = new Map();
      allHistoryEntries.forEach((entry) => {
        const recordId = entry.record_id ?? entry.matrix_record_id ?? entry.recordId ?? entry.approver_matrix_id;
        if (recordId == null) return;
        const key = String(recordId);
        const context = historyContextById.get(key) || {};
        const field = String(entry.column_changed || "").toLowerCase().replace(/\s+/g, "_");
        if (field === "plant_no" && entry.old_value) context.plant_no = entry.old_value;
        if (field === "plant_name" && entry.old_value) context.plant_name = entry.old_value;
        if (field === "plant" && entry.old_value) context.plant = entry.old_value;
        if (field === "country" && entry.old_value) context.country = entry.old_value;
        if (field === "city" && entry.old_value) context.city = entry.old_value;
        historyContextById.set(key, context);
      });

      const formatHistoryEntry = (entry, editTypeOverride) => {
        const rawSnapshot = entry.record_snapshot || entry.snapshot || entry.record_data || entry.old_record || entry.record || entry.data || {};
        let snapshot = rawSnapshot;
        if (typeof rawSnapshot === "string") {
          try {
            snapshot = JSON.parse(rawSnapshot);
          } catch {
            snapshot = {};
          }
        }
        const value = (field, ...aliases) =>
          entry[field] ?? aliases.reduce((result, alias) => result ?? snapshot?.[alias], undefined) ?? "";
        const recordId = entry.record_id ?? entry.matrix_record_id ?? entry.recordId ?? entry.approver_matrix_id;
        const currentRow = recordId == null ? {} : (currentRowsById.get(String(recordId)) || {});
        const historyContext = recordId == null ? {} : (historyContextById.get(String(recordId)) || {});
        const fallbackValue = (field, ...aliases) =>
          value(field, ...aliases) || currentRow[field] || historyContext[field] || "";
        const plantValue = fallbackValue("plant", "plant");
        const plantNo = fallbackValue("plant_no", "plant_no", "plantNo", "plant_number", "plantNumber") ||
          String(plantValue).match(/-(\d{3,})-/)?.[1] ||
          String(plantValue).match(/\((\d{3,})\)/)?.[1] ||
          String(entry.old_value || "").match(/^\d{3,}$/)?.[0] || "";
        const action = String(entry.action || entry.operation || entry.change_type || entry.edit_type || "").toLowerCase();
        const summary = String(entry.modified_summary || entry.comments || entry.change_log || "");
        const isDeleted = /delete|deleted/.test(action) || /record\s+deleted/i.test(summary);
        const isBulk = /bulk/.test(action) || /bulk/.test(summary);
        return {
          "Plant Name": fallbackValue("plant_name", "plant_name", "plantName"),
          "Plant No": plantNo,
          "Plant": plantValue,
          "Country": fallbackValue("country", "country"),
          "City": fallbackValue("city", "city"),
          "Edit Type": editTypeOverride || (isDeleted ? "Record Deleted" : (isBulk ? "Bulk Edit" : (entry.edit_type || "Individual Edit"))),
          "Field Changed": entry.column_changed?.replace(/_/g, " ") || entry.modified_summary || "",
          "Old Value": entry.old_value || "",
          "New Value": entry.new_value || "",
          "Changed By": entry.changed_by || entry.deleted_by || "",
          "Date & Time": entry.change_datetime || entry.deleted_at
            ? new Date(entry.change_datetime || entry.deleted_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })
            : "",
        };
      };

      const deletedGroups = new Map();
      const nonDeletedEntries = [];
      allHistoryEntries.forEach((entry) => {
        const action = String(entry.action || entry.operation || entry.change_type || entry.edit_type || "").toLowerCase();
        const summary = String(entry.modified_summary || entry.comments || entry.change_log || "");
        const isDeleted = /delete|deleted/.test(action) || /record\s+deleted/i.test(summary);
        if (!isDeleted) {
          nonDeletedEntries.push(entry);
          return;
        }

        const recordId = entry.record_id ?? entry.matrix_record_id ?? entry.recordId ?? entry.approver_matrix_id ?? "unknown";
        const timestamp = entry.change_datetime || entry.deleted_at || entry.created_at || "";
        const key = `${recordId}|${timestamp}|${entry.changed_by || entry.deleted_by || ""}`;
        const group = deletedGroups.get(key) || { entries: [], first: entry };
        group.entries.push(entry);
        deletedGroups.set(key, group);
      });

      const deletedRows = Array.from(deletedGroups.values()).map(({ entries, first }) => {
        const row = formatHistoryEntry(first, "Record Deleted");
        const fields = entries
          .map((entry) => entry.column_changed?.replace(/_/g, " "))
          .filter(Boolean);
        const values = entries
          .filter((entry) => entry.column_changed)
          .map((entry) => `${entry.column_changed.replace(/_/g, " ")}: ${entry.old_value || ""}`);
        return {
          ...row,
          "Field Changed": fields.join(", "),
          "Old Value": values.join("\n"),
          "New Value": "",
        };
      });

      const exportRows = nonDeletedEntries
        .map((entry) => formatHistoryEntry(entry))
        .concat(deletedRows, bulkRows)
        .sort((left, right) => {
          const leftTime = Date.parse(left["Date & Time"] || "");
          const rightTime = Date.parse(right["Date & Time"] || "");
          if (Number.isNaN(leftTime)) return 1;
          if (Number.isNaN(rightTime)) return -1;
          return rightTime - leftTime;
        });

      const ws = XLSX.utils.json_to_sheet(exportRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Change History");
      const today = new Date().toISOString().split("T")[0];
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
        `Change_History_${today}.xlsx`
      );
    } catch (error) {
      console.error("History export error:", error);
      alert("Failed to export history. Please try again.");
    } finally {
      setHistoryExporting(false);
    }
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      let allRows = [];
      let currentPage = 1;
      const pageSize = 500;
      let hasMore = true;

      while (hasMore) {
        const params = new URLSearchParams({
          page: String(currentPage),
          page_size: String(pageSize),
          _timestamp: String(Date.now()),
        });

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/plant-approver-matrix?${params.toString()}`,
          {
            method: "GET",
            headers: {
              ...getAuthHeaders(),
              "Cache-Control": "no-cache",
              Pragma: "no-cache",
            },
            cache: "no-store",
          }
        );

        const body = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(body.detail || body.message || `HTTP ${response.status}`);
        }

        const rows = Array.isArray(body.data) ? body.data : [];
        allRows = allRows.concat(rows);
        hasMore = body.pagination?.has_next || false;
        currentPage++;
      }

      if (allRows.length === 0) {
        alert("No data available to export.");
        return;
      }

      // Remove internal 'id' field from export
      const exportRows = allRows.map((row) => {
        const { id, ...rest } = row;
        return rest;
      });

      // Write with uppercase headers for visual differentiation
      const headerKeys = Object.keys(exportRows[0] || {});
      const uppercaseRows = exportRows.map((row) => {
        const newRow = {};
        headerKeys.forEach((key) => {
          newRow[key.toUpperCase()] = row[key];
        });
        return newRow;
      });

      const worksheet = XLSX.utils.json_to_sheet(uppercaseRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Plant Approver Matrix");

      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const today = new Date().toISOString().split("T")[0];
      saveAs(blob, `Plant_Approver_Matrix_${today}.xlsx`);
    } catch (error) {
      console.error("Export error:", error);
      alert("Failed to export data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    const parsedPage = Number(newPage);

    if (
      !Number.isInteger(parsedPage) ||
      parsedPage < 1 ||
      parsedPage === pagination.page
    ) {
      return;
    }

    if (activeSearch) {
      fetchSearchResults(activeSearch, parsedPage, pagination.page_size);
    } else {
      fetchData(parsedPage, pagination.page_size).catch(() => {});
    }
  };

  const handlePageSizeChange = (newSize) => {
    const parsedSize = Number(newSize);

    if (
      !Number.isInteger(parsedSize) ||
      parsedSize < 1
    ) {
      return;
    }

    if (activeSearch) {
      fetchSearchResults(activeSearch, 1, parsedSize);
    } else {
      fetchData(1, parsedSize).catch(() => {});
    }
  };

  const toLabel = (key) => {
    return key
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\b\w/g, (character) =>
        character.toUpperCase()
      );
  };

  const formatFieldValue = (
    key,
    rawValue
  ) => {
    if (
      rawValue === null ||
      rawValue === undefined ||
      rawValue === ""
    ) {
      return "-";
    }

    const isDateField =
      DATE_FIELD_HINTS.some((hint) =>
        key.toLowerCase().includes(hint)
      );

    if (isDateField) {
      const parsedDate = new Date(rawValue);

      if (!Number.isNaN(parsedDate.getTime())) {
        return parsedDate.toLocaleDateString(
          "en-GB"
        );
      }
    }

    return String(rawValue);
  };

  const columns = useMemo(() => {
    if (data.length === 0) {
      return [];
    }

    const cellStyle = [
      "display:inline-block",
      "max-width:160px",
      "overflow:hidden",
      "text-overflow:ellipsis",
      "white-space:nowrap",
      "font-size:14px",
      "vertical-align:middle",
    ].join(";");

    const allKeys = Object.keys(data[0]).filter(
      (key) => !HIDDEN_FIELDS.includes(key)
    );

    const generatedColumns = allKeys.map(
      (key) => ({
        id: key,
        label: toLabel(key),
        width: "180px",
        noWrap: true,
        sortDisabled: true,
        cellTemplate: (event) => {
          const value = formatFieldValue(
            key,
            event.rowData[key]
          );

          return event.html`
            <span
              title="${value}"
              style="${cellStyle}"
            >
              ${value}
            </span>
          `;
        },
      })
    );

    // Prepend checkbox column when search is active (for row selection / bulk edit)
    if (activeSearch && canEdit) {
      generatedColumns.unshift({
        id: "__select__",
        label: "✓",
        width: "52px",
        noWrap: true,
        sortDisabled: true,
        cellTemplate: (event) => {
          const rowId = String(event.rowData.id);
          const isChecked = __currentSelectedIds.has(rowId);
          return event.html`
            <input
              type="checkbox"
              class="row-checkbox"
              .checked="${isChecked}"
              @change="${(e) => {
                e.stopPropagation();
                window.dispatchEvent(
                  new CustomEvent("toggleRowSelection", { detail: { rowId } })
                );
              }}"
            />
          `;
        },
      });
    }

    if (canEdit) {
      generatedColumns.push({
        id: "actions",
        label: "Actions",
        noWrap: true,
        width: "160px",
        sortDisabled: true,
        cellTemplate: (event) => {
          const rowId = event.rowData.id;

          const trashIcon = `
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6" />
              <path d="M14 11v6" />
              <path d="M9 6V4h6v2" />
            </svg>
          `;

          return event.html`
            <div
              style="
                display:flex;
                gap:6px;
                align-items:center;
              "
            >
              <button
                type="button"
                class="matrix-history-btn"
                title="View change log"
                @click="${() =>
                  window.dispatchEvent(
                    new CustomEvent(
                      "openMatrixHistory",
                      {
                        detail: {
                          rowId,
                        },
                      }
                    )
                  )}"
              >
                <mc-icon icon="file-magnifying-glass" size="16"></mc-icon>
              </button>

              <button
                type="button"
                class="matrix-edit-btn"
                @click="${() =>
                  window.dispatchEvent(
                    new CustomEvent(
                      "openMatrixEdit",
                      {
                        detail: {
                          rowId,
                        },
                      }
                    )
                  )}"
              >
                Edit
              </button>

              ${canDelete ? event.html`<button
                type="button"
                class="matrix-delete-btn"
                title="Delete row"
                .innerHTML="${trashIcon}"
                @click="${() =>
                  window.dispatchEvent(
                    new CustomEvent(
                      "deleteMatrixRow",
                      {
                        detail: {
                          rowId,
                        },
                      }
                    )
                  )}"
              ></button>` : ""}
            </div>
          `;
        },
      });
    }

    return generatedColumns;
  }, [canEdit, canDelete, data, activeSearch]);

  return (
    <div className="approver-wrapper mds">
      <McTopBar
        product="Maersk | Unplanned Charges"
        productshort="Maersk"
        className="app-topbar"
      >
        <div
          slot="actions"
          className="topbar-actions mds"
        >
          <McButton
            onClick={() => navigate("/")}
            label="Back to Home"
            appearance="secondary"
            fit="small"
          />

          <McButton
            onClick={handleLogout}
            label="Sign Out"
            appearance="secondary"
            fit="small"
          />
        </div>
      </McTopBar>

      <div className="approver-container">
        <div className="content-wrapper mds">
          <div className="am-header-row">
            <div className="am-header-left">
              <h3 className="main-header">
                Plant Approver Matrix
              </h3>

              {!loading && (
                <p className="am-subtitle">
                  {pagination.total_records.toLocaleString()}{" "}
                  records total
                </p>
              )}
            </div>

            {/* ── Search bar (centre) ─────────────────────────────────── */}
            <div className="am-header-centre mds">
              <McInput
                label=""
                placeholder="Search by plant, country, approver, segment…"
                value={searchQuery}
                fit="small"
                clearbutton
                autocomplete="off"
                input={(e) =>
                  setSearchQuery(e.target.value ?? e.detail ?? "")
                }
                keydown={(e) => {
                  if (
                    e.detail?.key === "Enter" ||
                    e.key === "Enter"
                  )
                    handleSearch();
                }}
                style={{ minWidth: "260px" }}
              />
              <McButton
                label={searchLoading ? "Searching…" : "Search"}
                fit="small"
                appearance="primary"
                click={handleSearch}
                disabled={searchQuery.trim().length < 2 || searchLoading}
              />
              {activeSearch && (
                <McButton
                  label="Clear"
                  fit="small"
                  appearance="secondary"
                  click={handleSearchClear}
                />
              )}
              {activeSearch && selectedRowIds.size > 0 && canEdit && (
                <McButton
                  label={`Bulk Edit (${selectedRowIds.size} selected)`}
                  fit="small"
                  appearance="primary"
                  click={openBulkEdit}
                />
              )}
            </div>

            {/* ── Action buttons (right) ──────────────────────────────── */}
            <div className="am-header-right mds">
              {canEdit && (
                <McButton
                  label="+ Add"
                  appearance="primary"
                  fit="small"
                  click={openAddDrawer}
                  disabled={loading}
                />
              )}
              <McButton
                label={loading ? "Exporting..." : "Export"}
                appearance="secondary"
                fit="small"
                click={handleExport}
                disabled={loading || data.length === 0}
              />
              <McButton
                label={historyExporting ? "Downloading..." : "Download History"}
                appearance="secondary"
                fit="small"
                click={handleHistoryExport}
                disabled={historyExporting || loading}
              />
            </div>
          </div>

          {activeSearch && (
            <div className="am-search-badge-row">
              <span className="am-search-badge">
                Results for: <strong>{activeSearch}</strong>
                {searchError && (
                  <span className="am-search-error"> — {searchError}</span>
                )}
              </span>
            </div>
          )}

          {/* ── Select-all row (search mode only) ────────────────────────── */}
          {activeSearch && data.length > 0 && canEdit && (
            <div className="am-select-all-row">
              <label className="am-select-all-label">
                <input
                  type="checkbox"
                  checked={
                    selectedRowIds.size === data.length &&
                    data.length > 0
                  }
                  onChange={handleSelectAll}
                />
                {selectedRowIds.size === data.length && data.length > 0
                  ? "Deselect All"
                  : `Select All (${data.length})`}
              </label>
              {selectedRowIds.size > 0 && (
                <span className="am-selected-count">
                  {selectedRowIds.size} of {data.length} selected
                </span>
              )}
            </div>
          )}

          {/* ── Bulk update result summary ────────────────────────────────── */}
          {bulkUpdateResult && (
            <div className="bulk-result-summary">
              <div className="bulk-result-header">
                <span className="bulk-result-title">Bulk Update Complete</span>
                <button
                  className="bulk-result-dismiss"
                  onClick={() => setBulkUpdateResult(null)}
                  aria-label="Dismiss"
                >
                  &#x2715;
                </button>
              </div>
              <div className="bulk-result-counts">
                <span className="bulk-count success">
                  {bulkUpdateResult.success_count} updated
                </span>
                {bulkUpdateResult.error_count > 0 && (
                  <span className="bulk-count error">
                    {bulkUpdateResult.error_count} error
                    {bulkUpdateResult.error_count !== 1 ? "s" : ""}
                  </span>
                )}
                {bulkUpdateResult.skipped_count > 0 && (
                  <span className="bulk-count skipped">
                    {bulkUpdateResult.skipped_count} skipped
                  </span>
                )}
              </div>
              {bulkUpdateResult.results
                ?.filter((r) => r.status !== "success")
                .map((r, i) => (
                  <div key={i} className="bulk-result-row">
                    <span className={`bulk-row-status ${r.status}`}>
                      {r.status}
                    </span>
                    Plant No: <strong>{r.plant_no}</strong> — {r.message}
                  </div>
                ))}
            </div>
          )}

          {fetchError && (
            <div className="page-error">
              {fetchError}
            </div>
          )}

          <div
            className={
              `approver-table-container` +
              `${loading || searchLoading ? " table-loading" : ""}`
            }
          >
            {(loading || searchLoading) && (
              <div className="table-loader-overlay">
                <div className="table-loader">
                  <div className="am-loader" />
                  <span className="loading-text">
                    Loading data...
                  </span>
                </div>
              </div>
            )}

            <McTable
              key={tableKey}
              data={data}
              datakey="id"
              zebrastripes
              headersticky
              truncate
              columnresize
              recordsperpage={
                data.length ||
                pagination.page_size
              }
              columns={columns}
              style={{
                border: "none",
              }}
            />

            {!loading && data.length === 0 && (
              <div className="no-records">
                No records found.
              </div>
            )}
          </div>

          <div className="pagination-section">
            <div className="pagination-stats">
              <span className="results-count">
                {pagination.total_records.toLocaleString()}{" "}
                records
              </span>

              <span className="page-info">
                Page {pagination.page} of{" "}
                {pagination.total_pages || 1}
              </span>
            </div>

            <div className="pagination-controls">
              <McSelect
                label="Per page"
                value={String(
                  pagination.page_size
                )}
                optionselected={(event) => {
                  const selectedValue =
                    event.detail?.value ??
                    event.detail;

                  handlePageSizeChange(
                    selectedValue
                  );
                }}
              >
                <McOption value="25">
                  25
                </McOption>

                <McOption value="50">
                  50
                </McOption>

                <McOption value="100">
                  100
                </McOption>

                <McOption value="200">
                  200
                </McOption>
              </McSelect>

              <div className="am-prev-next mds">
                <McButton
                  label="Prev"
                  appearance="secondary"
                  fit="small"
                  disabled={pagination.page <= 1 || loading}
                  click={() => handlePageChange(pagination.page - 1)}
                />
                <span className="am-page-label">
                  Page {pagination.page} of {pagination.total_pages || 1}
                </span>
                <McButton
                  label="Next"
                  appearance="secondary"
                  fit="small"
                  disabled={!pagination.has_next || pagination.page >= (pagination.total_pages || 1) || loading}
                  click={() => handlePageChange(pagination.page + 1)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {editRow && (
        <div
          className={
            `drawer-backdrop` +
            `${drawerOpen ? " backdrop-visible" : ""}`
          }
          onClick={closeEdit}
        />
      )}

      <div
        className={
          `edit-drawer mds` +
          `${drawerOpen ? " drawer-open" : ""}`
        }
      >
        {editRow && (
          <>
            <div className="drawer-header">
              <div>
                <div className="drawer-title">
                  Edit Approver Details
                </div>

                <div className="drawer-subtitle">
                  {editRow.plant_name ||
                    editRow.plant_no ||
                    "Selected Row"}
                </div>
              </div>

              <button
                type="button"
                className="drawer-close-btn"
                onClick={closeEdit}
                disabled={saving}
                aria-label="Close edit drawer"
              >
                &#x2715;
              </button>
            </div>

            <div className="drawer-body">
              <div className="drawer-info-card">
                {Object.entries(editRow)
                  .filter(
                    ([key]) => key !== "id" && key !== "comments"
                  )
                  .map(([key, value]) => {
                    const isEditable =
                      EDITABLE_FIELDS.includes(key);

                    const isEditing =
                      Boolean(
                        editingFields[key]
                      );

                    const originalValue =
                      formatFieldValue(
                        key,
                        value
                      );

                    const pendingValue =
                      isEditable &&
                      editValues[key] !==
                        undefined
                        ? editValues[key]
                        : null;

                    const normalizedOriginal =
                      String(value ?? "").trim();

                    const normalizedPending =
                      String(
                        pendingValue ?? ""
                      ).trim();

                    const isChanged =
                      pendingValue !== null &&
                      normalizedOriginal !==
                        normalizedPending;

                    const shownValue =
                      pendingValue !== null
                        ? pendingValue || "-"
                        : originalValue;

                    return (
                      <div
                        className={
                          `drawer-info-row` +
                          `${
                            isEditing
                              ? " row-editing"
                              : ""
                          }`
                        }
                        key={key}
                      >
                        <span className="info-label">
                          {toLabel(key)}
                        </span>

                        {isEditing ? (
                          <div className="inline-edit-group">
                            <input
                              className="inline-field-input"
                              value={
                                editValues[key] ??
                                ""
                              }
                              autoFocus
                              onChange={(event) => {
                                const newValue =
                                  event.target.value;

                                setEditValues(
                                  (previousValues) => ({
                                    ...previousValues,
                                    [key]: newValue,
                                  })
                                );
                              }}
                              onKeyDown={(event) => {
                                if (
                                  event.key ===
                                  "Enter"
                                ) {
                                  setEditingFields(
                                    (
                                      previousFields
                                    ) => ({
                                      ...previousFields,
                                      [key]: false,
                                    })
                                  );
                                }

                                if (
                                  event.key ===
                                  "Escape"
                                ) {
                                  setEditValues(
                                    (
                                      previousValues
                                    ) => ({
                                      ...previousValues,
                                      [key]:
                                        editRow[
                                          key
                                        ] ?? "",
                                    })
                                  );

                                  setEditingFields(
                                    (
                                      previousFields
                                    ) => ({
                                      ...previousFields,
                                      [key]: false,
                                    })
                                  );
                                }
                              }}
                            />

                            <button
                              type="button"
                              className="inline-done-btn"
                              title="Apply field change"
                              onClick={() => {
                                setEditingFields(
                                  (
                                    previousFields
                                  ) => ({
                                    ...previousFields,
                                    [key]: false,
                                  })
                                );
                              }}
                            >
                              &#x2713;
                            </button>
                          </div>
                        ) : (
                          <div className="info-value-group">
                            <span
                              className={
                                `info-value` +
                                `${
                                  isChanged
                                    ? " value-changed"
                                    : ""
                                }`
                              }
                            >
                              {shownValue}

                              {isChanged && (
                                <span className="changed-badge">
                                  edited
                                </span>
                              )}
                            </span>

                            {isEditable && (
                              <button
                                type="button"
                                className="pencil-btn"
                                title={`Edit ${toLabel(
                                  key
                                )}`}
                                onClick={() => {
                                  setEditingFields(
                                    (
                                      previousFields
                                    ) => ({
                                      ...previousFields,
                                      [key]: true,
                                    })
                                  );
                                }}
                              >
                                &#9998;
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                <div
                  className={
                    `drawer-info-row` +
                    `${
                      editingFields.__comments
                        ? " row-editing"
                        : ""
                    }`
                  }
                >
                  <span className="info-label">
                    Comments
                  </span>

                  {editingFields.__comments ? (
                    <div className="inline-edit-group">
                      <input
                        className="inline-field-input"
                        value={
                          editValues.comments ??
                          ""
                        }
                        placeholder="Reason for this change..."
                        autoFocus
                        onChange={(event) => {
                          setEditValues(
                            (previousValues) => ({
                              ...previousValues,
                              comments:
                                event.target.value,
                            })
                          );
                        }}
                        onKeyDown={(event) => {
                          if (
                            event.key === "Enter" ||
                            event.key === "Escape"
                          ) {
                            setEditingFields(
                              (
                                previousFields
                              ) => ({
                                ...previousFields,
                                __comments: false,
                              })
                            );
                          }
                        }}
                      />

                      <button
                        type="button"
                        className="inline-done-btn"
                        title="Apply comment"
                        onClick={() => {
                          setEditingFields(
                            (previousFields) => ({
                              ...previousFields,
                              __comments: false,
                            })
                          );
                        }}
                      >
                        &#x2713;
                      </button>
                    </div>
                  ) : (
                    <div className="info-value-group">
                      <span className="info-value">
                        {editValues.comments ||
                          "-"}
                      </span>

                      <button
                        type="button"
                        className="pencil-btn"
                        title="Add comment"
                        onClick={() => {
                          setEditingFields(
                            (previousFields) => ({
                              ...previousFields,
                              __comments: true,
                            })
                          );
                        }}
                      >
                        &#9998;
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {saveError && (
                <div className="drawer-error">
                  {saveError}
                </div>
              )}

              {saveSuccess && (
                <div className="drawer-success">
                  Changes saved successfully.
                </div>
              )}
            </div>

            <div className="drawer-footer">
              <McButton
                label="Cancel"
                appearance="secondary"
                fit="small"
                click={closeEdit}
                disabled={saving}
              />

              <McButton
                label={saving ? "Saving..." : "Save Changes"}
                appearance="primary"
                fit="small"
                click={handleSave}
                disabled={saving}
              />
            </div>
          </>
        )}
      </div>

      {deleteRow && (
        <div className="delete-overlay mds">
          <div className="delete-dialog">
            <div className="delete-dialog-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#c62828"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="3 6 5 6 21 6" />

                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />

                <path d="M10 11v6" />

                <path d="M14 11v6" />

                <path d="M9 6V4h6v2" />
              </svg>
            </div>

            <div className="delete-dialog-title">
              Delete Record
            </div>

            <div className="delete-dialog-msg">
              Are you sure you want to delete{" "}
              <strong>
                {deleteRow.plant_name ||
                  deleteRow.plant_no ||
                  "this record"}
              </strong>
              ?
              <br />
              This action cannot be undone.
            </div>

            {deleteError && (
              <div className="drawer-error delete-error">
                {deleteError}
              </div>
            )}

            <div className="delete-dialog-actions">
              <McButton
                label="Cancel"
                appearance="secondary"
                fit="small"
                click={() => {
                  setDeleteRow(null);
                  setDeleteError("");
                }}
                disabled={deleting}
              />

              <McButton
                label={deleting ? "Deleting..." : "Yes, Delete"}
                appearance="error"
                fit="small"
                click={handleDelete}
                disabled={deleting}
              />
            </div>
          </div>
        </div>
      )}

      {/* Add Row Drawer */}
      {addDrawerOpen && (
        <div className="drawer-backdrop backdrop-visible" onClick={closeAddDrawer} />
      )}

      <div className={`edit-drawer mds${addDrawerOpen ? " drawer-open" : ""}`}>
        {addDrawerOpen && (
          <>
            <div className="drawer-header">
              <div>
                <div className="drawer-title">Add New Record</div>
                <div className="drawer-subtitle">Fill in the fields below</div>
              </div>
              <button
                type="button"
                className="drawer-close-btn"
                onClick={closeAddDrawer}
                disabled={adding}
                aria-label="Close add drawer"
              >
                &#x2715;
              </button>
            </div>

            <div className="drawer-body">
              <div className="add-form-card">
                <p className="add-form-section-title">Record Details</p>
                {EDITABLE_FIELDS.map((field) => (
                  <div className="add-form-field" key={field}>
                    {field === "plant" ? (
                      <>
                        <McInput
                          label="Plant (Auto-generated)"
                          value={addValues[field] ?? ""}
                          placeholder="Filled automatically from Plant Name, Plant No, Country, City"
                          fit="small"
                          width="100"
                          readonly
                          autocomplete="off"
                        />
                        <span style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>
                          Auto-generated as: Plant Name – Plant No – Country – City
                        </span>
                      </>
                    ) : (
                      <McInput
                        label={field.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                        value={addValues[field] ?? ""}
                        placeholder={`Enter ${field.replace(/_/g, " ")}`}
                        fit="small"
                        width="100"
                        required
                        clearbutton
                        autocomplete="off"
                        input={(e) =>
                          setAddValues((prev) => ({ ...prev, [field]: e.target.value ?? e.detail ?? "" }))
                        }
                      />
                    )}
                  </div>
                ))}

                <hr className="add-form-divider" />

                <div className="add-form-field">
                  <McInput
                    label="Comments"
                    value={addValues.comments ?? ""}
                    placeholder="Enter comments (optional)"
                    fit="small"
                    width="100"
                    clearbutton
                    autocomplete="off"
                    input={(e) =>
                      setAddValues((prev) => ({ ...prev, comments: e.target.value ?? e.detail ?? "" }))
                    }
                  />
                </div>
              </div>

              {addError && (
                <div className="drawer-error">{addError}</div>
              )}

              {addSuccess && (
                <div className="drawer-success">Record added successfully.</div>
              )}
            </div>

            <div className="drawer-footer">
              <McButton
                label="Cancel"
                appearance="secondary"
                fit="small"
                click={closeAddDrawer}
                disabled={adding}
              />
              <McButton
                label={adding ? "Saving..." : "Add Record"}
                appearance="primary"
                fit="small"
                click={handleAddSave}
                disabled={adding}
              />
            </div>
          </>
        )}
      </div>

      {/* ── Bulk Edit Drawer ─────────────────────────────────────────────── */}
      <div
        className={`drawer-backdrop${bulkEditOpen ? " backdrop-visible" : ""}`}
        onClick={closeBulkEdit}
      />
      <div className={`edit-drawer mds${bulkEditOpen ? " drawer-open" : ""}`}>
        {bulkEditOpen && (
          <>
            <div className="drawer-header">
              <div>
                <div className="drawer-title">Bulk Edit</div>
                <div className="drawer-subtitle">
                  {selectedRowIds.size} record
                  {selectedRowIds.size !== 1 ? "s" : ""} selected
                </div>
              </div>
              <button
                type="button"
                className="drawer-close-btn"
                onClick={closeBulkEdit}
                disabled={bulkUpdating}
                aria-label="Close bulk edit drawer"
              >
                &#x2715;
              </button>
            </div>

            <div className="drawer-body">
              <p className="bulk-edit-hint">
                Choose which fields to update and enter the new value.
                The <strong>same value</strong> will be applied to all
                {selectedRowIds.size} selected record
                {selectedRowIds.size !== 1 ? "s" : ""}.
                Empty values are ignored.
              </p>

              <div className="bulk-field-list">
                {BULK_EDIT_FIELDS.map((field) => {
                  const current = bulkCurrentValues[field.dbField];
                  return (
                  <div className="bulk-field-item" key={field.dbField}>
                    <label className="bulk-field-toggle">
                      <input
                        type="checkbox"
                        checked={!!bulkEditFields[field.dbField]}
                        onChange={(e) =>
                          setBulkEditFields((prev) => ({
                            ...prev,
                            [field.dbField]: e.target.checked,
                          }))
                        }
                      />
                      <span className="bulk-field-label">{field.label}</span>
                    </label>



                    {bulkEditFields[field.dbField] && (
                      field.dbField === "plant" ? (
                        <>
                          <input
                            className="bulk-field-input"
                            placeholder="Auto-generated from Plant Name, Plant No, Country, City"
                            value={bulkEditValues[field.dbField] ?? ""}
                            readOnly
                            style={{ background: "#f1f5f9", cursor: "not-allowed", color: "#64748b" }}
                          />
                          <span style={{ fontSize: "11px", color: "#64748b", marginTop: "2px", display: "block" }}>
                            Auto-generated as: Plant Name – Plant No – Country – City
                          </span>
                        </>
                      ) : (
                        <input
                          className="bulk-field-input"
                          placeholder={`New ${field.label}…`}
                          value={bulkEditValues[field.dbField] ?? ""}
                          onChange={(e) =>
                            setBulkEditValues((prev) => ({
                              ...prev,
                              [field.dbField]: e.target.value,
                            }))
                          }
                        />
                      )
                    )}
                  </div>
                  );
                })}
              </div>

              {bulkUpdateError && (
                <div className="drawer-error">{bulkUpdateError}</div>
              )}
            </div>

            <div className="drawer-footer">
              <McButton
                label="Cancel"
                appearance="secondary"
                fit="small"
                click={closeBulkEdit}
                disabled={bulkUpdating}
              />
              <McButton
                label={
                  bulkUpdating
                    ? "Updating…"
                    : `Apply to ${selectedRowIds.size} Record${selectedRowIds.size !== 1 ? "s" : ""}`
                }
                appearance="primary"
                fit="small"
                click={handleBulkUpdate}
                disabled={bulkUpdating}
              />
            </div>
          </>
        )}
      </div>

      {/* ── History Modal (MDS McModal) ────────────────────────────────── */}
      <McModal
        className="mds"
        open={historyOpen}
        heading={`Change Log — ${historyRow?.plant_name || historyRow?.plant_no || "Record"}`}
        width="fit-content"
        style={{ "--mc-modal-max-width": "1100px", "--mc-modal-min-height": "520px" }}
        backdropcloseactiondisabled={historyLoading}
        escapecloseactiondisabled={historyLoading}
        closed={() => { setHistoryOpen(false); setHistoryRow(null); setHistoryPage(1); }}
      >
        <div style={{ minHeight: "120px" }}>
          {historyLoading && (
            <div className="history-loading">
              <div className="am-loader" />
              <span className="loading-text">Loading history…</span>
            </div>
          )}

          {historyError && !historyLoading && (
            <div className="drawer-error">{historyError}</div>
          )}

          {!historyLoading && !historyError && (() => {
            // Bulk edit rows: sourced from modified_summary + comments on the row itself
            const isBulk = /bulk/i.test(historyRow?.comments || "") ||
                           /bulk/i.test(historyRow?.change_log || "");
            const bulkRows = [];
            if (isBulk && historyRow?.modified_summary) {
              historyRow.modified_summary
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
                .forEach((entry, i) => {
                  // entry format: "field: old → new" or "field: value"
                  const colonIdx = entry.indexOf(":");
                  const fieldName = colonIdx !== -1 ? entry.slice(0, colonIdx).trim() : entry;
                  const changeDesc = colonIdx !== -1 ? entry.slice(colonIdx + 1).trim() : "";
                  const arrowIdx = changeDesc.search(/→|->|\?/);
                  const oldVal = arrowIdx !== -1 ? changeDesc.slice(0, arrowIdx).trim() : null;
                  const newVal = arrowIdx !== -1 ? changeDesc.slice(arrowIdx + 1).replace(/^\s*[>]?\s*/, "").trim() : changeDesc;
                  bulkRows.push({
                    _key: `bulk-${i}`,
                    _isBulk: true,
                    column_changed: fieldName,
                    old_value: oldVal,
                    new_value: newVal || historyRow.comments,
                    changed_by: historyRow.last_updated_by || "—",
                    change_datetime: historyRow.last_updated_timestamp || null,
                  });
                });
            } else if (isBulk) {
              bulkRows.push({
                _key: "bulk-0",
                _isBulk: true,
                column_changed: "Multiple fields",
                old_value: null,
                new_value: historyRow?.comments || historyRow?.change_log || "—",
                changed_by: historyRow?.last_updated_by || "—",
                change_datetime: historyRow?.last_updated_timestamp || null,
              });
            }

            const allRows = [...bulkRows, ...historyData].filter((item) =>
              item.column_changed || item.old_value || item.new_value || item.changed_by || item.change_datetime
            );
            const totalPages = Math.max(1, Math.ceil(allRows.length / HISTORY_PAGE_SIZE));
            const safePage = Math.min(historyPage, totalPages);
            const pagedRows = allRows.slice((safePage - 1) * HISTORY_PAGE_SIZE, safePage * HISTORY_PAGE_SIZE);

            if (allRows.length === 0) {
              return (
                <div className="history-empty">
                  <McIcon icon="file-magnifying-glass" size="32" />
                  <p>No changes recorded for this record.</p>
                </div>
              );
            }

            return (
              <>
              <div className="history-table-wrapper" ref={historyTableWrapperRef}>
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Field</th>
                      <th>Old Value</th>
                      <th>New Value</th>
                      <th>Changed By</th>
                      <th>Date &amp; Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map((item, idx) => {
                      const globalIdx = (safePage - 1) * HISTORY_PAGE_SIZE + idx;
                      const dt = item.change_datetime
                        ? new Date(item.change_datetime).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })
                        : "—";
                      return (
                        <tr key={item._key ?? item.history_id ?? globalIdx}>
                          <td className="history-td-num">{globalIdx + 1}</td>
                          <td>
                            {item.column_changed?.replace(/_/g, " ") || "—"}
                          </td>
                          <td className="history-td-old">{item.old_value || <em className="history-empty-val">—</em>}</td>
                          <td className="history-td-new">{item.new_value || <em className="history-empty-val">—</em>}</td>
                          <td className="history-td-by">{item.changed_by || "—"}</td>
                          <td className="history-td-dt">{dt}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {allRows.length > 0 && (
                <div className="history-pagination">
                  <span className="history-page-info">
                    {allRows.length} records &nbsp;&bull;&nbsp; Page {safePage} of {totalPages}
                  </span>
                  <div className="history-page-btns">
                    <button
                      className="history-page-btn"
                      disabled={safePage === 1}
                      onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                    >
                      &#8592; Prev
                    </button>
                    <button
                      className="history-page-btn"
                      disabled={safePage === totalPages}
                      onClick={() => setHistoryPage((p) => Math.min(totalPages, p + 1))}
                    >
                      Next Page &#8594;
                    </button>
                  </div>
                </div>
              )}
              </>
            );
          })()}
        </div>

        <McButton
          slot="secondaryAction"
          appearance="neutral"
          fit="medium"
          dialogaction="cancel"
          click={() => { setHistoryOpen(false); setHistoryRow(null); setHistoryPage(1); }}
          disabled={historyLoading}
        >
          Close
        </McButton>
      </McModal>
    </div>
  );
};


export default Approvermatrix;